import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());

  const refresh = useCallback(async () => {
    if (!user) { setIds(new Set()); return; }
    try {
      const r = await api.get("/favorites/ids");
      setIds(new Set(r.data.ids));
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isFav = useCallback((id) => ids.has(id), [ids]);

  const toggle = useCallback(async (id) => {
    if (!user) return { needAuth: true };
    const wasFav = ids.has(id);
    setIds((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(id); else next.add(id);
      return next;
    });
    try {
      if (wasFav) await api.delete(`/favorites/${id}`);
      else await api.post(`/favorites/${id}`);
    } catch {
      setIds((prev) => {
        const next = new Set(prev);
        if (wasFav) next.add(id); else next.delete(id);
        return next;
      });
      return { error: true };
    }
    return { favorited: !wasFav };
  }, [ids, user]);

  return (
    <FavoritesContext.Provider value={{ ids, isFav, toggle, refresh }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => useContext(FavoritesContext);
